<template>
    <span v-if="props.signup === ''" data-test="signup" class="text-slate-500">-</span>
    <vx-popover v-else hover teleport arrow placement="top" class="min-w-0">
        <span data-test="signup">{{ signupLabels[props.signup] }}</span>
        <template v-slot:content>
            <div class="flex max-w-[260px] flex-col gap-2">
                <div data-test="signup-explanation">{{ explanation }}</div>
                <vx-button
                    v-if="props.signup === 'invite_expired' && props.ownerInviteId !== null"
                    type="button"
                    data-test="signup-resend-invite"
                    size="sm"
                    color="muted"
                    block
                    :loading="resending"
                    @click="resendInvite"
                >
                    Resend invite <font-awesome-icon :icon="faEnvelope" />
                </vx-button>
            </div>
        </template>
    </vx-popover>
</template>

<script lang="ts" setup>
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxPopover } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { groupInvitationsClient } from '~/services';
import type { KioskTeamGroupSignup } from '~/services/adminTeamGroupsClient.types';
import { fromNow } from '~/utils/date';
import { catchRequestError } from '~/utils/request';
import { signupLabels } from './kioskTeamGroupsTableHelpers';

const props = defineProps<{
    teamId: number;
    groupId: string;
    signup: KioskTeamGroupSignup;
    owner: string;
    ownerEmail: string;
    ownerInviteId: number | null;
    ownerLastLoginAt: string;
    ownerPredatesLoginRecords: boolean;
}>();

const emit = defineEmits<{
    resent: [];
}>();

const toasted = useToasted();

const resending = ref(false);

const ownerLabel = computed(() => (props.ownerEmail === '' ? props.owner : `${props.owner} (${props.ownerEmail})`));

const explanation = computed(() => {
    switch (props.signup) {
        case 'not_started':
            return 'No owner invited yet.';
        case 'invited':
            return `Invite sent to ${props.ownerEmail}, not accepted yet.`;
        case 'invite_expired':
            return `Invite to ${props.ownerEmail} expired after 14 days.`;
        case 'inactive':
            return props.ownerPredatesLoginRecords
                ? `${ownerLabel.value} hasn't signed in since sign-in records began, but may have before.`
                : `${ownerLabel.value} has never signed in.`;
        case 'signed':
            return `${ownerLabel.value} last signed in ${fromNow(props.ownerLastLoginAt)}.`;
        default:
            return '';
    }
});

const resendInvite = () => {
    if (props.ownerInviteId === null) {
        return;
    }

    resending.value = true;

    groupInvitationsClient
        .resendInvitation(props.teamId, props.groupId, props.ownerInviteId)
        .then(() => {
            toasted.success(`Invite resent to ${props.ownerEmail}`);
            emit('resent');
        })
        .catch((error) => toasted.error(catchRequestError(error)))
        .finally(() => {
            resending.value = false;
        });
};
</script>
